import { useTranslation } from 'react-i18next';
import { Anchor, CrowdinButton, Icon, SIDEBAR_WIDTH_LEFT } from '@anytag/shared-atoms';
import { Logo } from '@anytag/shared-molecules';
import { LanguageSelector, MenuItem } from '@anytag/shared-organisms';
import { MenuTitle } from '@anytag/router';
import { useGetHelpPageUrl } from '@anytag/shared-hooks';
import { useAuthData } from '@anytag/auth';
import { cn } from '@anytag/shared-utils';
import { getStepStyles, StepType, useTiktokSignupSteps } from './useSignupSteps';

export const TikTokLayoutMenu = () => {
  const { stepOne, stepTwo, stepThree } = useTiktokSignupSteps();
  const { t } = useTranslation();
  const helpCenterUrl = useGetHelpPageUrl();
  const { defaultRoute } = useAuthData();

  return (
    <div
      style={{ width: SIDEBAR_WIDTH_LEFT }}
      className="fixed z-11 box-border flex h-screen flex-col bg-white shadow-container"
    >
      <div className="box-border flex h-full flex-col p-4">
        <Logo type="black" to={defaultRoute} className="h-10 w-34.25" />

        <div className="relative mt-10 grid grid-rows-3 gap-10">
          {/* step 1 */}
          <Anchor to={stepOne.href} className={cn(styles.stepLink(stepOne))}>
            <span className={cn(styles.circleBase, getStepStyles(stepOne))}>
              {stepOne.isCompleted && !stepOne.isActive && (
                <Icon icon="check" variant="black" size={10} className="shrink-0" />
              )}
            </span>
            <div>
              <h3 className="font-semibold">{t('Account Registration')}</h3>
              <p>{t('Create your TikTok Account')}</p>
            </div>
          </Anchor>
          <div className={cn(styles.verticalLine, 'top-14.5 h-12')} />
          {/* step 2 */}
          <Anchor to={stepTwo.href} className={cn(styles.stepLink(stepTwo))}>
            <span className={cn(styles.circleBase, getStepStyles(stepTwo))}>
              {stepTwo.isCompleted && !stepTwo.isActive && (
                <Icon icon="check" variant="black" size={10} className="shrink-0" />
              )}
            </span>
            <div>
              <h3 className="mb-1 font-semibold">{t('appHeader.Select Plan')}</h3>
              <p>{t('Multiple Plans to select')}</p>
            </div>
          </Anchor>
          <div className={cn(styles.verticalLine, 'top-40.5 h-12')} />
          {/* step 3 */}
          <Anchor to={stepThree.href} className={cn(styles.stepLink(stepThree))}>
            <span className={cn(styles.circleBase, getStepStyles(stepThree))}>
              {stepThree.isCompleted && !stepThree.isActive && (
                <Icon icon="check" variant="black" size={10} className="shrink-0" />
              )}
            </span>
            <div>
              <h3 className="mb-1 font-semibold">{t('TikTok Ad Account')}</h3>
              <p>{t('Link and Create your Ad Account')}</p>
            </div>
          </Anchor>
        </div>
      </div>
      <CrowdinButton />
      <div className="border-t-border px-3 py-3.5">
        <LanguageSelector />
        <MenuItem
          key="help-center"
          target="_blank"
          size={22}
          title={MenuTitle.HelpCenter}
          href={helpCenterUrl}
          rightNode={<Icon icon="external-link" width={16} height={16} variant="gray" />}
        />
      </div>
    </div>
  );
};

const styles = {
  navigation: cn('box-border flex flex-1 flex-col px-6 py-4'),
  wrapper: cn(
    'box-sizing-border fixed z-11 box-border flex h-screen w-[var(--sidebar-width)] flex-1 flex-col bg-white px-6 py-4 shadow-container'
  ),
  stepsContainer: cn('relative mt-10 grid gap-10'),
  stepLink: (step: StepType) =>
    cn(
      'pointer-events-auto flex gap-x-3.5',
      step.isActive || step.isCompleted ? 'opacity-100' : 'opacity-30',
      step.isDisabled ? 'pointer-events-none' : 'pointer-events-auto'
    ),
  verticalLine: cn('absolute left-[11.5px] w-px bg-black-90 opacity-20'),
  circleBase: cn('relative box-border h-6 w-6 flex-none rounded-full border-base-black-60 border text-center')
};
